import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WhatsApp connection status. Cached 5min.
 */
export function useWhatsAppStatus() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['whatsapp', 'status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/whatsapp/status', getAuthHeader());
                return data;
            } catch {
                return { connected: false };
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * WhatsApp conversations list. Polls every 30s when enabled.
 */
export function useWhatsAppConversations(enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['whatsapp', 'conversations'],
        queryFn: async () => {
            const data = await apiFetch('/whatsapp/conversations', getAuthHeader());
            return Array.isArray(data) ? data : [];
        },
        enabled,
        staleTime: 15 * 1000,
        refetchInterval: 30 * 1000,
    });
}

/**
 * WhatsApp messages for a specific contact. Polls every 10s when active.
 */
export function useWhatsAppMessages(contactId, enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['whatsapp', 'messages', contactId],
        queryFn: async () => {
            const data = await apiFetch(`/whatsapp/conversations/${contactId}/messages?page_size=100`, getAuthHeader());
            return data?.messages || [];
        },
        enabled: !!contactId && enabled,
        staleTime: 5 * 1000,
        refetchInterval: 10 * 1000,
    });
}

/**
 * Send a WhatsApp message mutation.
 */
export function useSendWhatsAppMessage() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ contactId, body, messageType = 'text' }) => {
            const res = await axios.post(`${API}/whatsapp/send`, {
                contact_id: contactId,
                message_type: messageType,
                body,
            }, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate both conversations list and specific thread
            queryClient.invalidateQueries({ queryKey: ['whatsapp', 'conversations'] });
            queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages', variables.contactId] });
        },
    });
}
